// Client-side production permission mirror (Module 04 §7, slice 04-A).
//
// The server is the ONLY authorisation boundary: every §5 route is guarded by
// its permission code and the cell-level narrowings are re-checked in the
// service. This hook exists so the S1–S4 screens do not OVER-SHOW controls a
// role cannot use — a storekeeper opening a work order should see the Materials
// tab's issue button, not a Release button that 403s.
//
// It reads the caller's effective `production.*` codes off the permission list
// `GET /v1/auth/tenants` carries (D-586), so a tenant's clone of a seed role is
// offered what it holds. The table below mirrors `systemRoleGrants` in
// packages/contracts and answers only for a feed row without that list; the CI
// gate (`pnpm run perms:mirror`, tools/ci/web-permission-mirror.ts) diffs it
// cell-by-cell against the contract. `has()` is FAIL-CLOSED — false until the
// feed settles, false if it fails.

import {
  derivePermissions,
  holdsPermission,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/** The ten `production.*` codes slice 04-A registers (spec 04 §7, D-074),
 * sub-step 04-B-1's `production.floor.manage` (migration 0021 — the §7 "Manage
 * downtime reasons / shift defs / devices" row, first enforced by e24), and
 * sub-step 04-B-2's three (migration 0022): `production.floor.record` (e13),
 * `production.conflict.resolve` (e25 — the S7 conflicts screen),
 * `production.labor.adjust` (e31) — and slice 04-C-1's
 * `production.telemetry.read` (migration 0024: e22's downtime-log list, and —
 * since 04-C-2 mounted them — e20/e21's calibration drift reads and the S6
 * screen + S2 Calibration tab that ride them; one code because §7 gives the
 * reads one role set — GA/FA/planner/maintenance). Fifteen in all.
 * Slice 04-C-5 (migration 0029) adds the LAST TWO:
 * `production.approve_shift_handoff` — which was FORWARD-DECLARED and
 * deliberately absent from this mirror until a route checked it, because
 * mirroring a grant the server would not honour is exactly the drift the CI
 * gate exists to stop — and `production.qc_hold.release`. Nineteen in all. */
export const PRODUCTION_PERMISSIONS = [
  "production.read",
  "production.wo.write",
  "production.wo.release",
  "production.wo.complete",
  "production.wo.close",
  "production.wo.cancel",
  "production.operation.record",
  "production.material.issue",
  "production.view_costs",
  "production.cost.adjust",
  "production.floor.manage",
  "production.floor.record",
  "production.conflict.resolve",
  "production.labor.adjust",
  "production.telemetry.read",
  // 04-C-4: §7's scoreboard row (one code for a role set telemetry.read does
  // not have — it adds finance and qc) and §7's two piece-rate rows (one code,
  // identical columns; the qty/money split is a FIELD decision).
  "production.scoreboard.read",
  "production.piece_rate.read",
  // 04-C-5: §7's "Dispose defect hand-off" row (GA/FA/qc — e18's queue and
  // e19's decision) and e32's quality-hold release (the same role set, and
  // deliberately a SECOND code: `approve_shift_handoff` is grantable to a
  // custom role by name and a shipped pack does exactly that).
  "production.approve_shift_handoff",
  "production.qc_hold.release",
] as const;

export type ProductionPermission = (typeof PRODUCTION_PERMISSIONS)[number];

/**
 * Role code → `production.*` codes: the production slice of the contracts
 * `systemRoleGrants` table (packages/contracts/src/permissions.ts), transcribed
 * cell by cell. The CI mirror gate compares this table against the contract, so
 * edit the CONTRACT first and this second, never the other way round.
 *
 * The rows that are easy to get wrong:
 * - group_admin holds all fifteen INCLUDING `production.cost.adjust`,
 *   although the §7 matrix cell reads "—": the platform invariant is that
 *   group_admin holds every non-platform code (spec 16 §2.8). facility_admin
 *   does NOT hold it — the "Manual cost adjustment" row's only ✓ is finance.
 * - planner holds `production.wo.cancel` (§5 row 7: "facility_admin
 *   (draft/released also planner)") but NOT `production.wo.close` (that row is
 *   group_admin/facility_admin/finance) and NOT `production.material.issue`.
 * - floor_worker DOES hold `production.material.issue` from 04-B-2 (§7
 *   footnote ᴬ: 0019 withheld it while the only issue route was e30's
 *   office-side manual issue; 0022 grants it with e13's scan path — the
 *   endpoint that justifies it). e30 stays fenced from floor_worker at the
 *   service layer, so the OFFICE Materials-tab issue button still keys off
 *   role ≠ floor_worker there, not off this code alone.
 * - finance gains `production.labor.adjust` (§5 row 31: labor is money);
 *   planner does not.
 * - procurement holds NOTHING in this namespace — the only role with no
 *   `production.*` code at all.
 *
 * Cell-level narrowings ONE CODE CANNOT EXPRESS, all service-enforced (D-040
 * two-layer) and therefore NOT modelled here — the UI treats them as data the
 * server may still refuse or filter:
 * - `sales` reads NO work order through the list or the detail: its own-book
 *   arm is closed (D-290 ruling 9 — pegs exist now, and the old arm,
 *   `sales_order_line_id IS NOT NULL`, would have admitted every pegged work
 *   order in the tenant). A rep sees the work orders of their orders as the
 *   chips on the sales order detail (spec 09 §4.7); the real own-book fence is
 *   owed to spec 04 §7. The list's empty state says so to that reader
 *   (`readsOwnBookOnly` below), which is the one place this narrowing shows;
 * - `floor_worker` reads only operations at its own station, and the station
 *   queue resolves only its own department's stations;
 * - `planner` may cancel draft/released work orders only — the in_progress
 *   cancel (with disposition) is facility_admin/group_admin, so the UI mirrors
 *   the narrowing by not offering planner the mid-run cancel.
 */
export const PRODUCTION_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [...PRODUCTION_PERMISSIONS],
  facility_admin: [
    "production.read",
    "production.wo.write",
    "production.wo.release",
    "production.wo.complete",
    "production.wo.close",
    "production.wo.cancel",
    "production.operation.record",
    "production.material.issue",
    "production.view_costs",
    "production.floor.manage",
    "production.floor.record",
    "production.conflict.resolve",
    "production.labor.adjust",
    "production.telemetry.read",
    "production.scoreboard.read",
    "production.piece_rate.read",
    // 04-C-5: facility_admin is on §7's disposition row and on e32's.
    "production.approve_shift_handoff",
    "production.qc_hold.release",
  ],
  planner: [
    "production.read",
    "production.wo.write",
    "production.wo.release",
    "production.wo.complete",
    "production.wo.cancel",
    "production.operation.record",
    "production.view_costs",
    "production.telemetry.read",
    // 04-C-4: §7's scoreboard row has a planner ✓; its piece-rate rows do not.
    "production.scoreboard.read",
  ],
  sales: ["production.read"],
  procurement: [],
  finance: [
    "production.read",
    "production.wo.close",
    "production.view_costs",
    "production.cost.adjust",
    "production.labor.adjust",
    // 04-C-4: finance is on §7's scoreboard row AND both piece-rate rows.
    "production.scoreboard.read",
    "production.piece_rate.read",
  ],
  qc: [
    "production.read",
    "production.scoreboard.read",
    // 04-C-5: quality decides what happens to a defective drum (§7's
    // "Dispose defect hand-off" row) and clears the hold quality put on (e32).
    "production.approve_shift_handoff",
    "production.qc_hold.release",
  ],
  maintenance: ["production.read", "production.telemetry.read", "production.scoreboard.read"],
  storekeeper: ["production.read", "production.material.issue"],
  floor_worker: [
    "production.read",
    "production.floor.record",
    "production.material.issue",
    // 04-C-4: §7's scoreboard row's "wall-mode/floor variant" cell (the S12
    // board rides an ordinary floor session — D-087 ruling 1) and the two
    // piece-rate rows' "own rows only" cells (service-narrowed).
    "production.scoreboard.read",
    "production.piece_rate.read",
  ],
};

/** The effective `production.*` set (pure, unit-tested): the feed's
 * permission list cut to this slice, or the mirror without one (D-586). */
export function deriveProductionPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, PRODUCTION_ROLE_GRANTS);
}

/**
 * The system roles whose `production.read` reaches §7's OWN-BOOK breadth on
 * the server: the read fence's fallthrough (apps/api
 * src/modules/production/work-orders/wo-read-fence.ts), a grant carrying the
 * read code with no production office capability and no floor presence
 * (`sync.use_floor`). Of the ten system roles only `sales` is one — qc,
 * maintenance and storekeeper hold floor presence and read facility-wide.
 * That breadth admits NO work order until the book fence spec 04 §7 names as
 * owed (D-290 ruling 9), so such a reader's list is empty by construction.
 * A ROLE NAME list because role codes are the only caller signal the client
 * has; permissions.test.ts derives it from the contracts' own grants, so a
 * grant that moves a role across the line fails there.
 */
export const OWN_BOOK_READ_ROLES: ReadonlySet<string> = new Set(["sales"]);

/**
 * Does every role that gives this reader `production.read` resolve to the
 * own-book breadth, so the work-order list is empty for them whatever exists?
 * FALSE wherever the client cannot know: a role it does not recognise (a
 * tenant's custom role) may carry an office code or floor presence and read
 * facility-wide, so one such role makes the answer false, and so does any
 * system role that reads facility-wide. The one blind spot is scope, which
 * the role feed does not carry: a `sales` grant issued at a DEPARTMENT reads
 * that department's stations on the server. The sentence this gates (a
 * reader's customers' work orders show on each order's page) is true for that
 * reader too, since every reader of an order sees its work-order chips.
 */
export function readsOwnBookOnly(roles: RoleInput): boolean {
  const signal = "roleCodes" in roles ? roles : { roleCodes: roles, permissions: null };
  if (signal.permissions !== undefined && signal.permissions !== null) {
    // The server's own key (`wo-read-fence.ts`): the read code with no office
    // capability and no floor presence. Read off the union of the caller's
    // grants, so a second grant that reads wide makes the answer false, the
    // direction this function already takes when it cannot tell (D-586).
    const held = signal.permissions;
    return (
      held.has("production.read") &&
      !PRODUCTION_OFFICE_CODES.some((code) => held.has(code)) &&
      !held.has(FLOOR_PRESENCE_CODE)
    );
  }
  let ownBook = false;
  // LITERAL-ROLE-CODES: the pre-D-586 mirror path, for a feed row without a permission list.
  for (const role of signal.roleCodes) {
    const granted = PRODUCTION_ROLE_GRANTS[role];
    if (granted === undefined) return false;
    if (!granted.includes("production.read")) continue;
    if (!OWN_BOOK_READ_ROLES.has(role)) return false;
    ownBook = true;
  }
  return ownBook;
}

/**
 * Roles holding `identity.view_members` (contracts `systemRoleGrants` —
 * group_admin has every non-platform code; facility_admin every `identity.*`
 * except manage_roles / manage_security_policy). NOT a `production.*` code, so
 * it stays out of `PRODUCTION_ROLE_GRANTS` (whose CI mirror is against the
 * production slice) — S14's ledger needs it to decide whether the member
 * directory is readable AT ALL, because resolving a worker's NAME costs a
 * `GET /v1/members` that 403s for finance, the role that ledger exists for.
 * Module 06's inventory mirror carries the identical constant for the identical
 * reason; both are role NAMES because that is the only signal the client has.
 */
const MEMBER_DIRECTORY_ROLES = new Set(["group_admin", "facility_admin"]);

/** The production codes that make a `production.read` grant a facility-wide
 * reader, copied from the server's `PRODUCTION_OFFICE_CODES`
 * (`apps/api/src/modules/production/work-orders/wo-read-fence.ts`). Held equal
 * to it, with `FLOOR_PRESENCE_CODE`, by `tools/ci/web-permission-mirror.ts`.
 *
 * The client unions every membership's codes while the server judges each
 * grant on its own, so a user holding two memberships can read "facility-wide"
 * here where the server narrows one of them. That errs towards offering a list
 * the server then answers in full or in part, never towards hiding one. */
export const PRODUCTION_OFFICE_CODES: readonly string[] = [
  "production.wo.write",
  "production.wo.release",
  "production.wo.complete",
  "production.wo.close",
  "production.wo.cancel",
  "production.operation.record",
  "production.material.issue",
  "production.view_costs",
  "production.cost.adjust",
];

/** Module 18's floor-runtime capability, the fence's other wide key. */
export const FLOOR_PRESENCE_CODE = "sync.use_floor";

export interface ProductionPermissions extends AreaPermissions, RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  /** True when the caller may read `GET /v1/members` (worker display names). */
  readonly canReadMembers: boolean;
  /** True when the role feed itself failed — the screen says so and lets the
   * request go, rendering the server's own refusal rather than a blank page. */
  readonly isFeedError: boolean;
  /** True when the reader's work-order read is the sales own-book breadth
   * alone (`readsOwnBookOnly`): their list is empty by construction. */
  readonly readsOwnBookOnly: boolean;
}

export function useProductionPermissions(): ProductionPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveProductionPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    canReadMembers: holdsPermission(feed, "identity.view_members", MEMBER_DIRECTORY_ROLES),
    isFeedError: feed.isFeedError,
    readsOwnBookOnly: readsOwnBookOnly(feed),
  };
}
